import { LoadState, useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { getSignupFormSchema } from "@redotech/redo-model/marketing/schemas/get-signup-form-schema";
import { submitSignupFormStepSchema as submitSignupFormStep } from "@redotech/redo-model/marketing/schemas/submit-signup-form-step-schema";
import { logSignupFormAnalyticsSchema as logSignupFormAnalytics } from "@redotech/redo-model/marketing/schemas/view-signup-form-schema";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { CustomFieldProvider } from "@redotech/redo-web/marketing/form-elements/context";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import {
  RedoMarketingFormLocalData,
  useSignupFormAnalytics,
} from "@redotech/redo-web/marketing/signup-form/popup-form-instance";
import { SignupFormClient } from "@redotech/redo-web/marketing/signup-form/types";
import {
  generateAnonymousCustomerId,
  parseLocalData,
  submitFormStep,
} from "@redotech/redo-web/marketing/signup-form/utils";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { getCustomFieldOptions } from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-custom-field-options";
import { getEmbeddedForms } from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-embedded-forms";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  RedoShopifyRpcClientContext,
  RedoShopifyRpcClientContextProvider,
} from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";

const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Marketing embedded form");

  const elements = document.querySelectorAll(
    'div[id^="redo-marketing-embedded-form-"]',
  );

  const matchingElements = Array.from(elements).filter((element) => {
    return /^[a-zA-Z0-9]{5}$/.test(element.id.split("-").pop() ?? "");
  });

  log("Found Marketing embedded form container");

  if (matchingElements.length === 0 || !matchingElements[0]) {
    return;
  }

  // Shopify applies display: none to empty divs
  (matchingElements[0] as HTMLElement).style.display = "unset";

  const root = createRoot(matchingElements[0]);
  root.render(
    <StyleRoot container={matchingElements[0]} styleManager={STYLE_MANAGER}>
      <RedoRoot
        extensionShopifyClient={undefined}
        storeUrl={
          globalThis.redoStorefront?.storeUrl ??
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost
        }
      >
        <RedoShopifyRpcClientContextProvider
          baseUrl={
            globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
            process.env.REDO_SHOPIFY_SERVER_URL!
          }
          storeUrl={
            globalThis.Shopify?.shop ?? globalThis.Shopify?.Checkout?.apiHost
          }
          widgetId={globalThis.redoWidgetId}
        >
          <RedoMarketingEmbeddedForm
            autocheck={autocheck}
            formDivId={matchingElements[0].id}
          />
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );
}

function useFormData(
  formsLoad: LoadState<any[]>,
  rpcClient: RedoShopifyRpcClient | undefined,
  customerHash: string | undefined,
  formDivId: string,
): {
  form: ISignupForm | null;
  formId: string | null;
  merchantPhoneNumber: string | undefined;
} {
  const [form, setForm] = useState<ISignupForm | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [merchantPhoneNumber, setMerchantPhoneNumber] = useState<
    string | undefined
  >(undefined);

  if (formsLoad.value && rpcClient && customerHash && !form) {
    const matchingForm = formsLoad.value.find(
      (f: (typeof formsLoad.value)[number]) =>
        f.form?.behavior.embeddedCode.includes(formDivId),
    );

    if (matchingForm) {
      log("Form found", matchingForm);
      void rpcClient(logSignupFormAnalytics, {
        idempotencyKey: customerHash,
        formId: matchingForm.id,
        type: "view",
      }).catch((error: any) => {
        console.error("Error logging form view:", error);
      });

      setForm(matchingForm.form);
      setFormId(matchingForm.id);
      setMerchantPhoneNumber(matchingForm.merchantPhoneNumber);
    }
  }

  return { form, formId, merchantPhoneNumber };
}

function RedoMarketingEmbeddedForm({
  autocheck,
  formDivId,
}: {
  autocheck: WidgetAutocheck;
  formDivId: string;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useState<RedoMarketingFormLocalData>(() => {
    const stored = localStorage.getItem(SIGNUP_LOCAL_STORAGE_KEY);
    return stored ? parseLocalData(stored) : {};
  });

  // Generate customer hash if it doesn't exist (same pattern as popup form)
  useEffect(() => {
    if (!localData.customerHash) {
      const customerHash = generateAnonymousCustomerId();
      setLocalData((prev) => ({ ...prev, customerHash }));
    }
  }, [localData.customerHash]);

  const formsLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient(getEmbeddedForms, {});
    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, []);

  const { form, formId, merchantPhoneNumber } = useFormData(
    formsLoad,
    rpcClient,
    localData.customerHash,
    formDivId,
  );

  const signupFormClient: SignupFormClient | undefined = useMemo(() => {
    if (!rpcClient) {
      return;
    }
    return {
      getSignupForm: async (params, options) =>
        rpcClient(getSignupFormSchema, params, options),
      getCustomFieldOptions: async (params) =>
        rpcClient(getCustomFieldOptions, params),
      logSignupFormAnalytics: async (params) =>
        rpcClient(logSignupFormAnalytics, params),
      submitSignupFormStep: async (params) =>
        rpcClient(submitSignupFormStep, params),
    } satisfies SignupFormClient;
  }, [rpcClient]);

  const { logAnalytics } = useSignupFormAnalytics(
    signupFormClient,
    formId,
    localData,
    setLocalData,
  );

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      await submitFormStep({
        i,
        formData,
        form,
        formId,
        client: signupFormClient,
        localData,
        logAnalytics,
        setCurrentStep,
        entryPoint: MarketingEntryPoint.SIGNUP_FORM,
        skipPhoneSteps: true,
      });
    },
    [signupFormClient, localData, form, formId, logAnalytics],
  );

  if (formsLoad.pending) {
    return null;
  }

  if (formsLoad.error) {
    return null;
  }

  if (!form) {
    return null;
  }

  if (rpcClient) {
    return (
      <CustomFieldProvider
        getCustomFieldOptions={(id) =>
          rpcClient(getCustomFieldOptions, { customFieldId: id })
        }
      >
        <SignupForm
          brandKit={autocheck.brandKit}
          currentStep={currentStep}
          form={form}
          merchantPhoneNumber={merchantPhoneNumber}
          onSubmitStep={submitStep}
        />
      </CustomFieldProvider>
    );
  }

  return (
    <SignupForm
      brandKit={autocheck.brandKit}
      currentStep={currentStep}
      form={form}
      merchantPhoneNumber={merchantPhoneNumber}
      onSubmitStep={submitStep}
    />
  );
}
